import React, { useEffect, useMemo, useRef } from 'react';

import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { getCategoryAxisLabelLayout } from './chartPreviewUtils';
import type { ChartPreviewRequest } from './chartPreviewUtils';
import {
  applyCategoryAxisStyle,
  applyColumnCornerRadius,
  applyPreviewTooltipStyle,
  applyValueAxisStyle,
  buildXYTooltipHTML,
  getPreviewColors,
} from './chartPreviewStyles';
import useAmChartsResize from './useAmChartsResize';

type Props = {
  chart: ChartPreviewRequest;
  height?: number | string;
  width?: number | string;
};

const ColumnChartPreview = ({ chart, height, width }: Props): React.JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<am4charts.XYChart | null>(null);
  const theme = useTheme();
  useAmChartsResize(ref, chartRef);
  const colors = useMemo(() => getPreviewColors(theme), [theme]);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const { categoryField, valueFields, data } = chart;
    if (!categoryField || !valueFields?.length || !data?.length) {
      return;
    }

    const chartInstance = am4core.create(container, am4charts.XYChart);
    chartRef.current = chartInstance;
    chartInstance.data = data;
    chartInstance.colors.list = colors;
    chartInstance.numberFormatter.numberFormat = '$#,###.00';

    const containerWidth = container.getBoundingClientRect().width || container.clientWidth || container.offsetWidth;
    const { rotateLabels, minGridDistance, maxLabelWidth } = getCategoryAxisLabelLayout({
      data,
      categoryField,
      containerWidth,
    });

    const categoryAxis = chartInstance.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = categoryField;
    applyCategoryAxisStyle(categoryAxis, theme, {
      maxWidth: maxLabelWidth,
      minGridDistance,
      showGrid: false,
      showLine: true,
    });
    const categoryLabel = categoryAxis.renderer.labels.template;
    if (rotateLabels) {
      categoryLabel.rotation = -45;
      categoryLabel.horizontalCenter = 'right';
      categoryLabel.verticalCenter = 'middle';
    } else {
      categoryLabel.rotation = 0;
      categoryLabel.horizontalCenter = 'middle';
      categoryLabel.verticalCenter = 'top';
    }
    categoryLabel.interactionsEnabled = true;
    categoryLabel.tooltipText = '{category}';
    categoryLabel.adapter.add('tooltipText', (text, target) => {
      if (!target.isOversized) {
        return undefined;
      }
      const axisDataItem = target.dataItem;
      if (axisDataItem instanceof am4charts.CategoryAxisDataItem) {
        return axisDataItem.category;
      }
      return text;
    });

    const valueAxis = chartInstance.yAxes.push(new am4charts.ValueAxis());
    applyValueAxisStyle(valueAxis, theme, { min: 0, showGrid: false, showLine: false });

    valueFields.forEach((field, index) => {
      const series = chartInstance.series.push(new am4charts.ColumnSeries());
      series.dataFields.categoryX = categoryField;
      series.dataFields.valueY = field;
      series.name = field;
      series.columns.template.width = am4core.percent(85);
      series.columns.template.maxWidth = 125;
      series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      series.columns.template.tooltipY = 0;

      applyColumnCornerRadius(series.columns.template.column, {
        direction: 'vertical',
        valueKey: 'valueY',
        radius: 4,
      });

      const seriesColor = colors[index % colors.length];
      series.columns.template.fill = seriesColor;
      series.columns.template.stroke = seriesColor;
      series.fill = seriesColor;

      series.columns.template.tooltipHTML = buildXYTooltipHTML(theme, {
        categoryKey: 'categoryX',
        valueKey: 'valueY',
      });
      series.columns.template.tooltip = new am4core.Tooltip();
      applyPreviewTooltipStyle(series.columns.template.tooltip, theme, {
        background: 'paper',
        opacity: 0.9,
        strokeWidth: 0,
        textColor: theme.palette.text.primary,
      });
    });

    const cursor = new am4charts.XYCursor();
    cursor.lineX.disabled = true;
    cursor.lineY.disabled = true;
    cursor.behavior = 'none';
    chartInstance.cursor = cursor;

    return () => {
      chartInstance.dispose();
      chartRef.current = null;
    };
  }, [chart, colors, theme]);

  return <Box ref={ref} sx={{ height: height ?? 240, width: width ?? '100%' }} />;
};

export default ColumnChartPreview;
